import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

const DEFAULT_DISCLAIMER = `En confirmant le paiement, je m'engage à :

• Rendre les locaux dans l'état initial où je les ai trouvés.
• Ranger meubles, chaises et matériel comme à mon arrivée.
• Laisser les toilettes propres et fonctionnelles.
• Sortir mes déchets ou les déposer dans les poubelles prévues.

⚠️ En cas de désordre, salissure ou rangement non effectué, un supplément forfaitaire de 30 EUR (nettoyage et remise en état) sera facturé automatiquement.`;

/**
 * Reusable confirmation dialog shown right before redirecting to Stripe / PayPal.
 * The body text is admin-editable from /admin → Images tab (`payment_disclaimer`).
 *
 * Props:
 *   billingComplete: bool — user has filled all 3 legal fields (company/VAT/address).
 *                          When false, the "Facture entreprise (Peppol)" radio is
 *                          disabled and a link to complete the profile is shown.
 *   invoiceType / setInvoiceType: controlled state lifted to the parent
 *                          ('private' | 'business'). Every payment produces
 *                          an official invoice — the choice determines only
 *                          whether a Peppol XML is joined and whether the
 *                          customer's company + VAT are shown on the PDF.
 */
export default function HouseRulesDialog({
  open,
  onClose,
  onConfirm,
  message,
  isLoading,
  summary,
  billingComplete = false,
  invoiceType = 'private',
  setInvoiceType,
}) {
  const { t } = useTranslation();
  const text = (message && String(message).trim()) || DEFAULT_DISCLAIMER;
  const [accepted, setAccepted] = useState(false);

  // Reset accepted state each time the dialog opens.
  // Default invoice type -> 'business' when the customer has already filled
  // their legal profile, else 'private'.
  useEffect(() => {
    if (open) {
      setAccepted(false);
      if (typeof setInvoiceType === 'function') {
        setInvoiceType(billingComplete ? 'business' : 'private');
      }
    }
  }, [open, billingComplete, setInvoiceType]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !isLoading) onClose(); }}>
      <DialogContent
        data-testid="house-rules-dialog"
        className="max-w-lg p-0 overflow-hidden bg-white border-2 border-[#1A3C34]"
      >
        {/* Header band */}
        <div className="bg-[#C77D63] px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div>
            <DialogTitle className="text-white font-heading text-lg leading-tight">
              {t('houseRules.title', { defaultValue: 'Important — règles d\'utilisation' })}
            </DialogTitle>
            <DialogDescription className="text-white/90 text-xs mt-0.5">
              {t('houseRules.subtitle', { defaultValue: 'Merci de lire avant de payer' })}
            </DialogDescription>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[55vh] overflow-y-auto">
          {/* Optional summary card (booking recap) — shown at the top so the
              user validates WHAT they're paying for before agreeing to the
              rules. Renders nothing if no summary is provided. */}
          {summary && (
            <div
              data-testid="house-rules-summary"
              className="mb-4 p-4 rounded-md bg-[#1A3C34]/5 border border-[#1A3C34]/20"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold mb-1">
                    {t('houseRules.summaryHeader', { defaultValue: 'Récapitulatif de votre réservation' })}
                  </p>
                  <p className="text-sm font-semibold text-[#1A3C34] leading-snug">
                    {summary.space_name}
                    {summary.sessions_count > 1 && (
                      <span className="ml-1.5 text-[11px] font-medium text-white bg-[#C77D63] rounded-full px-2 py-0.5 align-middle">
                        {summary.sessions_count} {t('houseRules.sessions', { defaultValue: 'sessions' })}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-[#1A3C34] mt-1 leading-relaxed">
                    {summary.time_label && <><span className="font-mono tabular-nums">{summary.time_label}</span> · </>}
                    {summary.dates_label}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold">
                    {t('houseRules.totalTTC', { defaultValue: 'Total TTC' })}
                  </p>
                  <p className="text-xl font-bold text-[#C77D63] tabular-nums leading-none mt-1">
                    {Number(summary.total).toFixed(2)} €
                  </p>
                </div>
              </div>
            </div>
          )}

          <p
            data-testid="house-rules-message"
            className="text-sm text-[#1A3C34] whitespace-pre-line leading-relaxed"
          >
            {text}
          </p>

          {/* Mandatory acceptance checkbox */}
          <label
            htmlFor="house-rules-accept"
            className={`mt-5 flex items-start gap-3 p-3 border-2 rounded-md cursor-pointer transition-colors ${
              accepted ? 'bg-[#1A3C34]/5 border-[#1A3C34]' : 'bg-white border-[#C77D63]/40 hover:border-[#C77D63]'
            }`}
          >
            <Checkbox
              id="house-rules-accept"
              data-testid="house-rules-checkbox"
              checked={accepted}
              onCheckedChange={(v) => setAccepted(!!v)}
              disabled={isLoading}
              className="mt-0.5 border-[#1A3C34] data-[state=checked]:bg-[#1A3C34] data-[state=checked]:border-[#1A3C34]"
            />
            <span className="text-sm text-[#1A3C34] font-medium leading-snug select-none">
              {t('houseRules.acceptLabel', {
                defaultValue: "Je m'engage à respecter ces règles et j'accepte le supplément de 30 EUR en cas de non-respect.",
              })}
            </span>
          </label>

          {/* Type de facture — chaque paiement génère une facture officielle F-.
              Choix : "privée" (PDF seul, envoyé chez comptable en BCC) vs
              "entreprise" (PDF + Peppol UBL XML, nécessite données légales). */}
          <div
            data-testid="invoice-type-block"
            className="mt-4 border-2 border-[#C77D63]/40 rounded-md overflow-hidden"
          >
            <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-[#C77D63] text-white">
              Type de facture
            </div>
            <div className="p-3 space-y-2 bg-white">
              <label
                htmlFor="invoice-type-private"
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  invoiceType === 'private'
                    ? 'bg-[#1A3C34]/5 border-[#1A3C34]'
                    : 'bg-white border-[#E2E2DF] hover:border-[#C77D63]'
                }`}
              >
                <input
                  type="radio"
                  id="invoice-type-private"
                  data-testid="invoice-type-private"
                  name="invoice-type"
                  value="private"
                  checked={invoiceType === 'private'}
                  onChange={() => setInvoiceType && setInvoiceType('private')}
                  disabled={isLoading}
                  className="mt-1 accent-[#1A3C34]"
                />
                <div className="text-sm text-[#1A3C34] leading-snug select-none">
                  <div className="font-semibold">Facture privée (particulier)</div>
                  <div className="text-xs text-[#737373] mt-0.5">
                    Identification par votre nom et votre email. PDF envoyé par email.
                  </div>
                </div>
              </label>

              <label
                htmlFor="invoice-type-business"
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  invoiceType === 'business'
                    ? 'bg-[#C77D63]/10 border-[#C77D63]'
                    : 'bg-white border-[#E2E2DF] hover:border-[#C77D63]'
                } ${!billingComplete ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  id="invoice-type-business"
                  data-testid="invoice-type-business"
                  name="invoice-type"
                  value="business"
                  checked={invoiceType === 'business'}
                  onChange={() => billingComplete && setInvoiceType && setInvoiceType('business')}
                  disabled={isLoading || !billingComplete}
                  className="mt-1 accent-[#C77D63]"
                />
                <div className="text-sm text-[#1A3C34] leading-snug select-none">
                  <div className="font-semibold">Facture entreprise (Peppol)</div>
                  <div className="text-xs text-[#737373] mt-0.5">
                    PDF + XML Peppol conforme e-invoicing Belgique 2026. Déductible fiscalement.
                  </div>
                  {!billingComplete && (
                    <div className="text-xs text-amber-700 mt-1 font-semibold">
                      ⚠️ Complétez d&apos;abord vos données légales (raison sociale, TVA, adresse) dans <em>Mon compte → Mes données</em>.
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="bg-[#F9F9F7] px-6 py-4 border-t border-[#E2E2DF] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            data-testid="house-rules-cancel"
            type="button"
            variant="outline"
            disabled={isLoading}
            onClick={onClose}
            className="h-11 border-[#E2E2DF] text-[#737373] hover:bg-white"
          >
            <X className="w-4 h-4 mr-1.5" />
            {t('houseRules.cancel', { defaultValue: 'Annuler' })}
          </Button>
          <Button
            data-testid="house-rules-confirm"
            type="button"
            disabled={isLoading || !accepted}
            onClick={onConfirm}
            className="h-11 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 disabled:bg-[#1A3C34]/30 disabled:cursor-not-allowed font-bold"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('common.loading', { defaultValue: 'Chargement...' })}
              </span>
            ) : (
              <>
                <Check className="w-4 h-4 mr-1.5" />
                {t('houseRules.confirm', { defaultValue: 'J\'accepte et je paie' })}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
